import { useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { appLinks } from 'src/routing/appLinks';
import type {
  createPurchaseOrder as CreatePurchaseOrderData,
  createPurchaseOrderFromTemplate as CreatePurchaseOrderFromTemplateData,
  createPurchaseOrderFromTemplateVariables as CreatePurchaseOrderFromTemplateVariables,
  createPurchaseOrderTemplate as CreatePurchaseOrderTemplateData,
  createPurchaseOrderTemplateVariables as CreatePurchaseOrderTemplateVariables,
  createPurchaseOrderVariables as CreatePurchaseOrderVariables,
} from 'types/graphql';

export const CREATE_PURCHASE_ORDER_MUTATION = gql`
  mutation createPurchaseOrder($input: CreatePurchaseOrderInput!) {
    createPurchaseOrder(input: $input) {
      id
      slug
    }
  }
`;

export const CREATE_PURCHASE_ORDER_TEMPLATE_MUTATION = gql`
  mutation createPurchaseOrderTemplate($input: CreateBlankPurchaseOrderTemplateInput!) {
    createBlankPurchaseOrderTemplate(input: $input) {
      id
      slug
    }
  }
`;

export const CREATE_PURCHASE_ORDER_FROM_TEMPLATE_MUTATION = gql`
  mutation createPurchaseOrderFromTemplate($orderTemplateId: Int!) {
    createPurchaseOrderFromTemplate(orderTemplateId: $orderTemplateId) {
      id
      slug
    }
  }
`;

export function useCreatePurchaseOrder() {
  const [createPurchaseOrder] = useMutation<CreatePurchaseOrderData, CreatePurchaseOrderVariables>(
    CREATE_PURCHASE_ORDER_MUTATION
  );
  const navigate = useNavigate();

  return () => {
    createPurchaseOrder({
      variables: { input: {} },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the purchase order.'),
      onCompleted: ({ createPurchaseOrder }) => {
        void navigate(appLinks.purchaseOrder({ slug: createPurchaseOrder.slug }));
      },
    });
  };
}

export function useCreatePurchaseOrderTemplate() {
  const [createPurchaseOrderTemplate] = useMutation<
    CreatePurchaseOrderTemplateData,
    CreatePurchaseOrderTemplateVariables
  >(CREATE_PURCHASE_ORDER_TEMPLATE_MUTATION);
  const navigate = useNavigate();

  return (name: string) => {
    createPurchaseOrderTemplate({
      variables: { input: { name } },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the purchase order template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createBlankPurchaseOrderTemplate;

      void navigate(appLinks.purchaseOrder({ slug }));
    });
  };
}

export function useCreatePurchaseOrderFromTemplate() {
  const [createPurchaseOrderFromTemplate] = useMutation<
    CreatePurchaseOrderFromTemplateData,
    CreatePurchaseOrderFromTemplateVariables
  >(CREATE_PURCHASE_ORDER_FROM_TEMPLATE_MUTATION);
  const navigate = useNavigate();

  return (orderTemplateId: number) => {
    createPurchaseOrderFromTemplate({
      variables: { orderTemplateId },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the purchase order from the template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createPurchaseOrderFromTemplate;
      void navigate(appLinks.purchaseOrder({ slug }));
    });
  };
}
